import { ReceiptText } from "lucide-react";
import LegalPage, {
  BulletList,
  Note,
  Paragraph,
  Steps,
  Strong,
  SubHeading,
  TextLink,
  type LegalSection,
} from "../../components/LegalPage";
import { COMPANY, CONTACT_PATH } from "../../constants/company.constants";

// Content adapted for HRx from Upscale BD Ltd.'s Terms & Refund Policy
// (https://upscalebd.com/refund-policy).

const sections: LegalSection[] = [
  {
    id: "introduction",
    title: "Introduction",
    content: (
      <>
        <Paragraph>
          These Terms & Refund Policy outline the rights, responsibilities, and
          conditions that apply when you purchase <Strong>HRx</Strong> from{" "}
          {COMPANY.name}. By completing a purchase, you acknowledge that you
          have read, understood, and agreed to these terms in full.
        </Paragraph>
        <Paragraph>
          HRx is a product of {COMPANY.name}, which offers digital products and
          services such as software solutions, online tools, system
          integrations, and access-based digital resources. All HRx purchases
          are governed by the terms stated herein. Please read this policy
          carefully before placing an order, as it defines your rights regarding
          delivery, refunds, and post-delivery support.
        </Paragraph>
      </>
    ),
  },
  {
    id: "order-payment",
    title: "Order Placement & Payment",
    content: (
      <BulletList
        items={[
          "Once a purchase is made, an order confirmation will be sent to your registered email address.",
          "All payments must be completed in full at the time of purchase unless otherwise agreed upon in writing.",
          "We accept payments through our authorized gateways only. Payments made outside official channels will not be recognized.",
          "By making a payment, you confirm that you are legally authorized to use the provided payment method and that the information shared is accurate and valid.",
        ]}
      />
    ),
  },
  {
    id: "delivery",
    title: "Delivery Timeline",
    content: (
      <>
        <Paragraph>
          We strive to ensure all purchased items are delivered in a timely and
          professional manner.
        </Paragraph>
        <Note title="Standard Delivery Time">
          All purchases are delivered within 10 (ten) business days from the
          date of confirmed payment.
        </Note>
        <BulletList
          items={[
            "Delivery includes providing full access to the purchased product, service credentials, relevant documentation, or any digital assets specified in the order.",
            "Delivery confirmation will be shared through email or customer portal notification once your order is ready for use.",
            "In rare cases where delivery requires additional verification or customization, you will be informed promptly with an updated delivery timeline.",
          ]}
        />
      </>
    ),
  },
  {
    id: "delay",
    title: "Delay in Delivery",
    content: (
      <>
        <Paragraph>
          Customer satisfaction is our top priority. If, due to unforeseen
          circumstances, we fail to deliver your purchased product or service
          within 10 business days, you are entitled to a{" "}
          <Strong>100% refund</Strong> of the total payment made — no questions
          asked.
        </Paragraph>
        <Paragraph>
          We will notify you in advance if there are any expected delays due to
          technical or operational reasons.
        </Paragraph>
      </>
    ),
  },
  {
    id: "refund",
    title: "Refund Policy",
    content: (
      <>
        <Paragraph>
          We maintain a transparent and fair refund process to ensure our
          customers' trust and satisfaction.
        </Paragraph>
        <SubHeading>Eligibility for Refund</SubHeading>
        <BulletList
          items={[
            "We fail to deliver your purchased item within the promised 10 business days.",
            "The refund request is made before the delivery confirmation is issued.",
          ]}
        />
        <SubHeading>Non-Refundable Circumstances</SubHeading>
        <Paragraph>
          Once the product or service is successfully delivered, no refund or
          cancellation will be accepted under any of the following
          circumstances:
        </Paragraph>
        <BulletList
          dotClassName="bg-gray-400"
          items={[
            "You decide not to use the delivered product or service.",
            "The product is incompatible with your internal systems or third-party platforms.",
            "You wish to modify, replace, or exchange the delivered service for another.",
          ]}
        />
        <Paragraph>
          All customers are encouraged to review product details and consult our
          team before purchasing to ensure the selected service aligns with
          their requirements.
        </Paragraph>
        <SubHeading>Refund Process</SubHeading>
        <Steps
          items={[
            <>
              Submit your refund request via email to{" "}
              <TextLink href={`mailto:${COMPANY.email}`}>
                {COMPANY.email}
              </TextLink>{" "}
              with your order ID, payment reference, and reason for refund.
            </>,
            "All approved refunds will be processed within 7 business days of verification.",
            "Refunds will be made to the original payment method used during purchase.",
          ]}
        />
      </>
    ),
  },
  {
    id: "support",
    title: "Post-Delivery Support",
    content: (
      <>
        <Paragraph>
          We provide ongoing technical assistance and after-sales support to
          help you use your purchased service effectively. Our support includes:
        </Paragraph>
        <BulletList
          items={[
            "Clarifying product documentation or setup instructions.",
            "Basic troubleshooting assistance.",
            "Guidance on access, usage, and maintenance within the defined service scope.",
          ]}
        />
        <Paragraph>
          Please note that custom feature development, third-party system
          compatibility issues, or on-site technical support are not covered
          under standard post-delivery support unless separately contracted.
        </Paragraph>
      </>
    ),
  },
  {
    id: "modifications",
    title: "Service Modifications",
    content: (
      <>
        <Paragraph>
          {COMPANY.name} reserves the right to enhance, modify, or discontinue
          any product or service without prior notice, provided such changes do
          not affect active purchases under the delivery process.
        </Paragraph>
        <Paragraph>
          For existing customers, we ensure continuity and fairness in case of
          any major structural or technical updates.
        </Paragraph>
      </>
    ),
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    content: (
      <>
        <Paragraph>
          {COMPANY.name} shall not be held liable for any indirect, incidental,
          or consequential damages arising from:
        </Paragraph>
        <BulletList
          items={[
            "The use or inability to use the purchased product.",
            "Loss of business, revenue, or data caused by third-party integrations or user-side technical issues.",
            "Misuse of login credentials or unauthorized access to the product after delivery.",
          ]}
        />
        <Paragraph>
          Our total liability, in any case, will not exceed the total amount
          paid for the specific purchase.
        </Paragraph>
      </>
    ),
  },
  {
    id: "updates",
    title: "Policy Updates",
    content: (
      <>
        <Paragraph>
          We may update this Terms & Refund Policy from time to time to reflect
          changes in our business practices or legal obligations.
        </Paragraph>
        <Paragraph>
          Any modifications will take effect immediately upon being posted on
          this page, and the "Effective Date" will be updated accordingly. You
          are encouraged to review this page periodically to stay informed of
          any changes.
        </Paragraph>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact Us",
    content: (
      <Paragraph>
        If you have any questions about this Privacy Policy or your data, please
        contact your organization's HR department or the HRx support team
        through our <TextLink href={CONTACT_PATH}>contact page</TextLink>.
      </Paragraph>
    ),
  },
];

const RefundPolicy = () => (
  <LegalPage
    badge={{ icon: ReceiptText, label: "Terms & Refunds" }}
    title="Terms & Refund Policy"
    description={`The rights, responsibilities, and conditions that apply when you purchase HRx from ${COMPANY.name} — covering delivery, refunds, and post-delivery support.`}
    dateLabel="Effective date"
    date="Oct 23, 2025"
    sections={sections}
  />
);

export default RefundPolicy;
